import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Phases of a KYC attempt, in roughly chronological order. Each drives an icon
 * and colour in the timeline UI.
 */
export type KycTracePhase =
  | 'system' // attempt lifecycle (start, open, close)
  | 'capture' // brdocs camera/upload lifecycle + method choice
  | 'classify' // local CNN document-type classification
  | 'ocr' // local / Textract OCR text extraction
  | 'qr' // QR / MRZ decode + signature verification
  | 'precheck' // pre-review: extracted-vs-stored comparison, score, modal outcome
  | 'submit' // /kyc_check submission
  | 'poll' // stage polling
  | 'result'; // terminal outcome (passed / failed / postpone / cancel)

export type KycTraceLevel = 'info' | 'success' | 'warn' | 'error';

/** One timestamped entry in a KYC attempt's trace. */
export interface KycTraceEvent {
  seq: number;
  /** Epoch ms. */
  t: number;
  /** Ms since the attempt started. */
  offsetMs: number;
  phase: KycTracePhase;
  level: KycTraceLevel;
  title: string;
  /** Structured context (scores, fields, ids, …). Rendered expandable. */
  detail?: Record<string, unknown> | null;
}

/**
 * Collects a full, timestamped trace of a single KYC attempt — every brdocs
 * action and diagnostic (method, model classification, OCR, QR), the pre-review
 * decision, the submission, and the polled outcome — so the whole flow can be
 * inspected as a timeline. A root singleton: the KYC modal writes to it and the
 * /kyc-test harness reads it live (and after the modal closes).
 */
@Injectable({ providedIn: 'root' })
export class KycTraceService {
  private readonly _events = new BehaviorSubject<KycTraceEvent[]>([]);
  readonly events$ = this._events.asObservable();

  private seq = 0;
  private startedAt: number | null = null;
  attemptLabel: string | null = null;
  /** Stable client id for this attempt — correlates the streamed batches (and pre-submit events). */
  attemptId: string | null = null;

  /** Begin a fresh trace, clearing the previous attempt. */
  start(label?: string): void {
    this.seq = 0;
    this.startedAt = Date.now();
    this.attemptLabel = label ?? null;
    this.attemptId = this.newId();
    this._events.next([]);
    this.log('system', 'info', 'KYC attempt started', label ? { label } : null);
  }

  private newId(): string {
    try {
      const c = (globalThis as any).crypto;
      if (c?.randomUUID) return c.randomUUID();
    } catch {
      /* fall through */
    }
    return `att-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /** Append one event. Auto-starts the clock if `start()` wasn't called. */
  log(
    phase: KycTracePhase,
    level: KycTraceLevel,
    title: string,
    detail?: Record<string, unknown> | null,
  ): void {
    const t = Date.now();
    if (this.startedAt == null) this.startedAt = t;
    const ev: KycTraceEvent = {
      seq: ++this.seq,
      t,
      offsetMs: t - this.startedAt,
      phase,
      level,
      title,
      detail: this.clean(detail),
    };
    this._events.next([...this._events.value, ev]);
  }

  clear(): void {
    this.seq = 0;
    this.startedAt = null;
    this.attemptLabel = null;
    this._events.next([]);
  }

  snapshot(): KycTraceEvent[] {
    return this._events.value;
  }

  /**
   * A size-bounded copy of the trace, safe to ship inside an analytics event payload (the event
   * pipeline runs over SQS, which caps messages at 256 KB). Keeps the most recent `maxEvents`
   * events and truncates any oversized detail value.
   */
  serialize(maxEvents = 300, maxValueLen = 400): KycTraceEvent[] {
    return this._events.value.slice(-maxEvents).map((e) => ({
      ...e,
      detail: e.detail ? this.trimDetail(e.detail, maxValueLen) : null,
    }));
  }

  /** Trimmed events with seq strictly greater than `sinceSeq` — one streamed batch's worth. */
  delta(sinceSeq: number, maxValueLen = 400): KycTraceEvent[] {
    return this._events.value
      .filter((e) => e.seq > sinceSeq)
      .map((e) => ({ ...e, detail: e.detail ? this.trimDetail(e.detail, maxValueLen) : null }));
  }

  private trimDetail(detail: Record<string, unknown>, maxLen: number): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(detail)) {
      if (typeof v === 'string') {
        out[k] = v.length > maxLen ? v.slice(0, maxLen) + '…' : v;
      } else if (typeof v === 'object' && v !== null) {
        let s: string;
        try {
          s = JSON.stringify(v);
        } catch {
          s = String(v);
        }
        out[k] = s.length > maxLen ? s.slice(0, maxLen) + '…' : v;
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  /** Drop null/undefined keys so the detail panel stays tidy; null overall when empty. */
  private clean(detail?: Record<string, unknown> | null): Record<string, unknown> | null {
    if (!detail) return null;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(detail)) {
      if (v !== undefined && v !== null && v !== '') out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  }
}
